import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MeteorObservable } from 'meteor-rxjs';
import { firstValueFrom, map, shareReplay, Subscription } from 'rxjs';

import {
  addDays,
  addMonths,
  differenceInCalendarMonths,
  isAfter,
  isBefore,
  startOfDay,
  startOfMonth,
  subDays,
} from 'date-fns';
import { Users } from 'server/shared/collections/users';
import { formatUserName } from 'server/shared/helpers/user/formatUserName';
import { Customer } from 'server/shared/models/customer';
import { PoolerUser } from 'server/shared/models/user';
import { CustomerService } from '../../services/customer.service';
import { AvailabilityMonth } from './availability-month.model';

let times = require('lodash/times');

enum AvailabilityEditViewEnum {
  LIST = 'LIST',
  CALENDAR = 'CALENDAR',
}

@Component({
  selector: 'availability-edit',
  templateUrl: 'availability-edit.html',
  styleUrls: ['availability-edit.scss'],
})
export class AvailabilityEditComponent implements OnInit, OnDestroy {
  user?: PoolerUser;
  userSubscription?: Subscription;

  isDispoEditingPooler: boolean = false;
  meteorLiveQueryHandlers: (Meteor.LiveQueryHandle | Tracker.Computation)[] =
    [];

  availabilityEditViewEnum = AvailabilityEditViewEnum;
  activeView: AvailabilityEditViewEnum = AvailabilityEditViewEnum.LIST;
  dispoCustomer?: Customer;

  private _monthsInFuture: number = 6;
  public months: Array<AvailabilityMonth> = [];

  hasPoolerDepartmentAvailabilityOverview$ =
    this.customerService.customersOfUser$.pipe(
      map((customers) =>
        customers?.some((c) => c.enablePoolerDepartmentAvailabilityOverview),
      ),
      shareReplay(1),
    );

  constructor(
    private ngZone: NgZone,
    private location: Location,
    private route: ActivatedRoute,
    private router: Router,
    private customerService: CustomerService,
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.queryParamsUserId) {
      this.dispoCustomer = await this.customerService.readyDispoCustomer;
      this.isDispoEditingPooler = true;
      this.userSubscription = MeteorObservable.subscribe(
        'myProfile',
        this.queryParamsUserId,
      ).subscribe(() => {
        this._setUser(this.queryParamsUserId);
        this.prepareMonths();
      });
      const liveQueryHandler = Users.find(this.queryParamsUserId).observe({
        changed: () => {
          this._setUser(this.queryParamsUserId);
        },
      });
      this.meteorLiveQueryHandlers.push(liveQueryHandler);
    } else {
      const tracker = Tracker.autorun(() => {
        const userId = Meteor.userId();
        if (!userId) return;
        this.ngZone.run(() => {
          this._setUser(userId);
        });
      });
      this.meteorLiveQueryHandlers.push(tracker);
      this.prepareMonths();
    }
    if (await firstValueFrom(this.hasPoolerDepartmentAvailabilityOverview$)) {
      let queryParams = this.route.snapshot.queryParams;
      if (queryParams.view === 'calendar') {
        this.activeView = AvailabilityEditViewEnum.CALENDAR;
      }
      this.route.queryParams.subscribe((params) => {
        if (params.view === 'calendar') {
          this.activeView = AvailabilityEditViewEnum.CALENDAR;
        } else {
          this.activeView = AvailabilityEditViewEnum.LIST;
        }
      });
    }
  }

  toggleView() {
    if (this.activeView === AvailabilityEditViewEnum.CALENDAR) {
      this.activeView = AvailabilityEditViewEnum.LIST;
      this.updateQueryParams({ view: 'list' }).then();
    } else {
      this.activeView = AvailabilityEditViewEnum.CALENDAR;
      this.updateQueryParams({ view: 'calendar' }).then();
    }
  }

  async updateQueryParams(queryParams: Record<string, string>) {
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      queryParamsHandling: 'merge', // remove to replace all query params by provided
    });
  }

  private _setUser(userId: string) {
    this.ngZone.run(() => {
      let user = Users.findOne(userId) as PoolerUser;
      if (user && user.role !== undefined) {
        this.user = user;
      }
    });
  }

  private get queryParamsUserId(): string {
    return this.route.snapshot.queryParams['userId'];
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    if (
      this.meteorLiveQueryHandlers &&
      this.meteorLiveQueryHandlers.length > 0
    ) {
      this.meteorLiveQueryHandlers.forEach((liveQueryHandler) => {
        liveQueryHandler.stop();
      });
    }
  }

  historyBack() {
    this.location.back();
  }

  private prepareMonths(): void {
    if (this.queryParamsUserId) {
      if (!this.dispoCustomer) return;
      let joinDate: Date | undefined = this.user?.customerInformation?.[
        this.dispoCustomer._id
      ]?.joinDate
        ? new Date(
            this.user.customerInformation[this.dispoCustomer._id].joinDate!,
          )
        : undefined;
      let leaveDate: Date | undefined = this.user?.customerInformation?.[
        this.dispoCustomer._id
      ]?.leaveDate
        ? new Date(
            this.user.customerInformation[this.dispoCustomer._id].leaveDate!,
          )
        : undefined;
      if (joinDate && leaveDate && joinDate > leaveDate) {
        this.months.push(
          new AvailabilityMonth(
            startOfDay(new Date()),
            undefined,
            addDays(leaveDate, 1),
            subDays(joinDate, 1),
          ),
        );
        times(this._monthsInFuture, (index: number) => {
          if (joinDate && leaveDate) {
            this.months.push(
              new AvailabilityMonth(
                addMonths(startOfMonth(new Date()), index + 1),
                undefined,
                addDays(leaveDate, 1),
                subDays(joinDate, 1),
              ),
            );
          }
        });
      } else {
        if (!joinDate || isBefore(joinDate, new Date())) {
          joinDate = new Date();
        }
        this.months.push(
          new AvailabilityMonth(startOfDay(joinDate), leaveDate),
        );
        if (isAfter(startOfMonth(joinDate), startOfMonth(new Date()))) {
          this._monthsInFuture =
            this._monthsInFuture -
            differenceInCalendarMonths(
              startOfMonth(joinDate),
              startOfMonth(new Date()),
            );
        }
        times(this._monthsInFuture, (index: number) => {
          this.months.push(
            new AvailabilityMonth(
              addMonths(startOfMonth(joinDate || new Date()), index + 1),
              leaveDate,
            ),
          );
        });
      }
    } else {
      this.months.push(new AvailabilityMonth(startOfDay(new Date())));
      times(this._monthsInFuture, (index: number) => {
        this.months.push(
          new AvailabilityMonth(addMonths(startOfMonth(new Date()), index + 1)),
        );
      });
    }
  }

  formatUserName(user: PoolerUser): string {
    return formatUserName(user, this.dispoCustomer);
  }

  trackByStartDateString(_: number, month: AvailabilityMonth): string {
    return month.startDateString;
  }
}
